/**
 * Permission Utilities
 *
 * Shared permission-related utilities and types used across vendor and customer modules.
 */

import type { GlobalToken } from 'antd';

/**
 * Base item permission input interface
 * Extend this for entity-specific item permissions
 */
export interface BaseItemPermissionInput {
  isActive?: boolean;
  isPrimary?: boolean;
  isDefault?: boolean;
}

/**
 * Vendor-specific item permission input
 */
export interface VendorItemPermissionInput extends BaseItemPermissionInput {
  isDefaultReceiving?: boolean;
}

/**
 * Customer-specific item permission input
 */
export interface CustomerItemPermissionInput extends BaseItemPermissionInput {
  isDefaultShipping?: boolean;
  isDefaultBilling?: boolean;
}

/**
 * Combined item permission input type for generic use
 */
export type ItemPermissionInput = VendorItemPermissionInput & CustomerItemPermissionInput;

/**
 * Base item permissions interface
 */
export interface BaseItemPermissions {
  /** Whether this item can be edited */
  canEdit: boolean;
  /** Whether this item can be set as primary (contacts) */
  canSetPrimary: boolean;
  /** Whether this item can be set as default (locations, banks) */
  canSetDefault: boolean;
  /** Whether this item's status can be toggled */
  canToggleStatus: boolean;
  /** Whether this item can be deleted */
  canDelete: boolean;
  /** Whether this item is currently active (for UI display logic) */
  isItemActive: boolean;
  /** Whether this item is default/primary and active (cannot be deactivated) */
  isProtectedFromDeactivate: boolean;
}

/**
 * Customer-specific item permissions (extends base with shipping/billing defaults)
 */
export interface CustomerItemPermissions extends BaseItemPermissions {
  /** Whether this item can be set as default shipping (customer locations) */
  canSetDefaultShipping: boolean;
  /** Whether this item can be set as default billing (customer locations) */
  canSetDefaultBilling: boolean;
}

/**
 * Toggle status action style configuration
 * Returns the style object for the toggle status menu item
 */
export interface ToggleStatusStyleConfig {
  /** Whether to show danger (red) styling */
  danger: boolean;
  /** Whether the action is disabled */
  disabled: boolean;
  /** Style object for the menu item */
  style: { color: string } | undefined;
}

/**
 * Get toggle status action style configuration
 * Use this to ensure consistent styling across all toggle status actions
 *
 * @param itemPerms - Item permissions from getItemPermissions()
 * @param token - Ant Design theme token
 * @returns Style configuration for toggle status action
 *
 * @example
 * const itemPerms = getItemPermissions(record);
 * const toggleStyle = getToggleStatusStyle(itemPerms, token);
 * {
 *   key: 'toggleStatus',
 *   label: itemPerms.isItemActive ? t('vendor.actions.deactivate') : t('vendor.actions.activate'),
 *   ...toggleStyle,
 * }
 */
export function getToggleStatusStyle(
  itemPerms: Pick<BaseItemPermissions, 'isItemActive' | 'canToggleStatus'>,
  token: GlobalToken
): ToggleStatusStyleConfig {
  return {
    danger: itemPerms.isItemActive && itemPerms.canToggleStatus,
    disabled: !itemPerms.canToggleStatus,
    style: !itemPerms.canToggleStatus
      ? { color: token.colorTextDisabled }
      : !itemPerms.isItemActive
        ? { color: token.colorSuccess }
        : undefined,
  };
}

/**
 * Check if an item is protected from deactivation (is default or primary AND active)
 */
export function isItemProtectedFromDeactivate(item: ItemPermissionInput | null): boolean {
  if (!item) return false;
  const itemIsActive = item.isActive ?? true;
  const isDefaultOrPrimary =
    item.isPrimary ||
    item.isDefault ||
    item.isDefaultReceiving ||
    item.isDefaultShipping ||
    item.isDefaultBilling;
  return itemIsActive && !!isDefaultOrPrimary;
}

/**
 * Calculate base item permissions
 * Used by both vendor and customer permission hooks
 */
export function calculateBaseItemPermissions(
  entityIsActive: boolean,
  item: ItemPermissionInput | null
): BaseItemPermissions {
  const itemIsActive = item?.isActive ?? true; // Default to true for new items
  const isProtectedFromDeactivate = isItemProtectedFromDeactivate(item);

  return {
    // Can only edit if BOTH entity AND item are active
    canEdit: entityIsActive && itemIsActive,
    // Can only set as primary if entity is active AND item is active AND not already primary
    canSetPrimary: entityIsActive && itemIsActive && !item?.isPrimary,
    // Can only set as default if entity is active AND item is active AND not already default
    canSetDefault:
      entityIsActive && itemIsActive && !item?.isDefault && !item?.isDefaultReceiving,
    // Can toggle status if entity is active AND not protected (default/primary items cannot be deactivated)
    canToggleStatus: entityIsActive && !isProtectedFromDeactivate,
    // Can delete if entity is active (item status doesn't matter for delete)
    canDelete: entityIsActive,
    // Item status for UI display logic
    isItemActive: itemIsActive,
    // Whether item is protected from deactivation
    isProtectedFromDeactivate,
  };
}

/**
 * Calculate customer-specific item permissions (extends base with shipping/billing defaults)
 */
export function calculateCustomerItemPermissions(
  entityIsActive: boolean,
  item: CustomerItemPermissionInput | null
): CustomerItemPermissions {
  const base = calculateBaseItemPermissions(entityIsActive, item);
  const itemIsActive = item?.isActive ?? true;

  return {
    ...base,
    // Can only set as default shipping if entity is active AND item is active AND not already default shipping
    canSetDefaultShipping: entityIsActive && itemIsActive && !item?.isDefaultShipping,
    // Can only set as default billing if entity is active AND item is active AND not already default billing
    canSetDefaultBilling: entityIsActive && itemIsActive && !item?.isDefaultBilling,
  };
}
